import * as yup from 'yup';
import 'helpers/yup'; // registers yup.string().phone()
import { CONTACT_SCHEMA } from 'components/contact/common';
import { isFieldRequired, isValueEmpty, requiredMessage } from './required-fields';

export const SIGNATURE_REQUIRED_MESSAGE = 'Please add your signature!';

// On a web form, "required" comes only from the placement (`{{ Board.Field || formElement* }}`),
// which the server sends as `field.required`. The board field's own `optional: false` is not
// applied here, and core's /api/portal/forms/submit uses the same rule, so client and server
// agree on which fields block a submission.
export const isWebFormFieldRequired = (field) => isFieldRequired({ ...field, optional: undefined });

// Format checks. They only run once the field has a value — an empty field is judged by the
// required rule alone, so an empty optional number never shows "Please provide a valid number".
const getFormatSchema = (type, required) => {
  switch (type) {
    case 'email':
      return yup.string().trim().email('Invalid email');
    case 'url':
      return yup.string().trim().url('Enter a valid url (e.g. https://www.google.com/)');
    case 'tel':
      return yup.string().trim().phone('Invalid phone');
    case 'number':
      return yup.number().typeError('Please provide a valid number');
    case 'date':
    case 'datetime-local':
      return yup.date().typeError('Please provide a valid date');
    case 'person':
      // As before: a contact is only checked for completeness when the field is required.
      return required ? CONTACT_SCHEMA : yup.mixed();
    default:
      return yup.mixed();
  }
};

/**
 * The yup schema for one web form field. Whether a value counts as "filled in" is decided by
 * `isValueEmpty` (shared with core), so `0`, `"0"` and `"$0.00"` are real values, whitespace is
 * not, and `[]` / `{}` / an address without `formattedAddress` are empty. Booleans are never
 * required.
 */
export const getValidationSchemaForField = (field = {}) => {
  const { type } = field;

  // The signature placement is always required when the form has one.
  if (type === 'signature') {
    return yup
      .mixed()
      .test('required', SIGNATURE_REQUIRED_MESSAGE, (value) => !isValueEmpty(value, type));
  }

  const required = isWebFormFieldRequired(field);
  const message = requiredMessage(field);

  return yup.lazy((value) => {
    if (!isValueEmpty(value, type)) return getFormatSchema(type, required);
    const isBlank = value == null || (typeof value === 'string' && value.trim() === '');
    // Something typed that still doesn't count as a value ("abc" in a number field) gets the
    // format error ("Please provide a valid number"), not "is required".
    const schema = isBlank ? yup.mixed() : getFormatSchema(type, required);
    return schema.test('required', message, () => !required);
  });
};
